import { ActivityWebhook } from '@goldcast/api/backend';

import { State, MarketoCampaignFolder } from '@/studio/eventIntegration/store/types';
import { CrmObjectType, FieldMapping, FieldMappingRowError, SourceField } from '@/studio/orgIntegration/store/types';
import {
  getFieldMappingRowErrorDefaultState,
  getFieldMappingsDefaultState,
  getPrevFieldMappingsDefaultState,
  getSaveFieldMappingLoadingDefaultState
} from '@/studio/orgIntegration/store/utils';

export default {
  // Salesforce ******************************************************
  setIsSalesforceValidateCampaignIdLoading(state: State, isSalesforceValidateCampaignIdLoading: boolean) {
    state.isSalesforceValidateCampaignIdLoading = isSalesforceValidateCampaignIdLoading;
  },
  setConnectSalesforceCampaignLoading(state, isConnectSalesforceCampaignLoading) {
    state.isConnectSalesforceCampaignLoading = isConnectSalesforceCampaignLoading;
  },
  setIsSalesforceCampaignStatusLoading(state: State, isSalesforceCampaignStatusLoading: boolean) {
    state.isSalesforceCampaignStatusLoading = isSalesforceCampaignStatusLoading;
  },

  // Slack **************************************************************
  setSlackChannels(state: State, channels: any[]) {
    state.slackChannels = channels;
  },
  setSlackChannelsLoading(state: State, isSlackChannelsLoading: boolean) {
    state.isSlackChannelsLoading = isSlackChannelsLoading;
  },
  setSlackChannelError(state: State, error: any) {
    state.slackChannelError = error;
  },
  setValidateSlackChannelIDLoading(state: State, isSlackChannelValidationLoading: boolean) {
    state.isSlackChannelValidationLoading = isSlackChannelValidationLoading;
  },

  // Pardot ****************************************************************
  setPardotImportLoading(state: State, isPardotImportLoading: boolean) {
    state.isPardotImportLoading = isPardotImportLoading;
  },
  setPardotExportLoading(state: State, isPardotExportLoading: boolean) {
    state.isPardotExportLoading = isPardotExportLoading;
  },
  setPardotError(state: State, pardotError: any) {
    state.pardotError = pardotError;
  },

  // Wistia ***********************************************************
  setWistiaProjects(state: State, wistiaProjects: any[]) {
    state.wistiaProjects = [...wistiaProjects];
  },
  setIsWistiaLoading(state: State, isWistiaLoading: boolean) {
    state.isWistiaLoading = isWistiaLoading;
  },

  // Marketo ************************************************************
  setIsMarketoValidateProgramIdLoading(state: State, isMarketoValidateProgramIdLoading: boolean) {
    state.isMarketoValidateProgramIdLoading = isMarketoValidateProgramIdLoading;
  },
  setIsMarketoCampaignFoldersLoading(state: State, isMarketoCampaignFoldersLoading: boolean) {
    state.isMarketoCampaignFoldersLoading = isMarketoCampaignFoldersLoading;
  },
  setMarketoCampaignFolders(state: State, marketoCampaignFolders: MarketoCampaignFolder[]) {
    state.marketoCampaignFolders = marketoCampaignFolders;
  },
  setIsMarketoCloneLoading(state: State, isMarketoCloneLoading: boolean) {
    state.isMarketoCloneLoading = isMarketoCloneLoading;
  },
  setIsMarketoProgramStatusLoading(state: State, isMarketoProgramStatusLoading: boolean) {
    state.isMarketoProgramStatusLoading = isMarketoProgramStatusLoading;
  },

  // Webhooks ************************************************************
  setWebhooksLoading(state: State, isWebhooksLoading: boolean) {
    state.isWebhooksLoading = isWebhooksLoading;
  },
  setWebhooks(state: State, webhooks: ActivityWebhook[]) {
    state.webhooks = webhooks;
  },

  // Eloqua ************************************************************
  setIsEloquaValidateEventFormIdLoading(state: State, isEloquaValidateEventFormIdLoading: boolean) {
    state.isEloquaValidateEventFormIdLoading = isEloquaValidateEventFormIdLoading;
  },
  setIsValidEloquaFormConnected(state: State, isValid: boolean) {
    state.isValidEloquaFormConnected = isValid;
  },

  // SlackPlusSalesforce **************************************************
  setIsSlackPlusSalesforceNotificationsLoading(state: State, isSlackPlusSalesforceNotificationsLoading: boolean) {
    state.isSlackPlusSalesforceNotificationsLoading = isSlackPlusSalesforceNotificationsLoading;
  },
  setSlackPlusSalesforceNotifications(state: State, slackPlusSalesforceNotifications) {
    state.slackPlusSalesforceNotifications = slackPlusSalesforceNotifications;
  },

  // IntegrationsActivitySyncDetails ************************************************
  setIsIntegrationsActivitySyncDetailsLoading(state: State, isIntegrationsActivitySyncDetailsLoading: boolean) {
    state.isIntegrationsActivitySyncDetailsLoading = isIntegrationsActivitySyncDetailsLoading;
  },
  setIntegrationsActivitySyncDetails(state: State, integrationsActivitySyncDetails) {
    state.integrationsActivitySyncDetails = integrationsActivitySyncDetails;
  },
  setIntegrationsActivitySyncErrorState(state: State, integrationActivitySyncError) {
    state.integrationActivitySyncError = integrationActivitySyncError;
  },

  // Event Level field mapping ************************************************
  setEventSourceFieldLoading(state: State, isEventSourceFieldLoading: boolean) {
    state.isEventSourceFieldLoading = isEventSourceFieldLoading;
  },
  setEventSourceFields(state: State, eventSourceFields: SourceField[]) {
    state.eventSourceFields = eventSourceFields;
  },
  initializeEventFieldMappingStore(state: State, crmObjectTypes: CrmObjectType[]) {
    state.eventFieldMappings = getFieldMappingsDefaultState(crmObjectTypes);
    state.prevEventFieldMappings = getPrevFieldMappingsDefaultState(crmObjectTypes);
    state.eventFieldMappingRowError = getFieldMappingRowErrorDefaultState(crmObjectTypes);
    state.isSaveEventFieldMappingLoading = getSaveFieldMappingLoadingDefaultState(crmObjectTypes);
  },
  setEventFieldMappingLoading(
    state: State,
    { crmObjectType, isFieldMappingLoading }: { crmObjectType: string; isFieldMappingLoading: boolean }
  ) {
    if (crmObjectType in state.eventFieldMappings) {
      state.eventFieldMappings[crmObjectType].isLoading = isFieldMappingLoading;
    }
  },
  setEventFieldMappings(
    state: State,
    { crmObjectType, eventFieldMappings }: { crmObjectType: string; eventFieldMappings: FieldMapping[] }
  ) {
    if (crmObjectType in state.eventFieldMappings) {
      state.eventFieldMappings[crmObjectType].mappings = eventFieldMappings;
    }
  },
  setPrevEventFieldMappings(
    state: State,
    { crmObjectType, prevEventFieldMappings }: { crmObjectType: string; prevEventFieldMappings: FieldMapping[] }
  ) {
    state.prevEventFieldMappings[crmObjectType] = prevEventFieldMappings;
  },
  setEventFieldMappingRowError(
    state: State,
    { crmObjectType, errorsArray }: { crmObjectType: string; errorsArray: (FieldMappingRowError | null)[] }
  ) {
    state.eventFieldMappingRowError[crmObjectType] = errorsArray;
  },
  addNewEmptyEventFieldMappingItem(
    state: State,
    { crmObjectType, emptyFieldMapping }: { crmObjectType: string; emptyFieldMapping: FieldMapping }
  ) {
    if (crmObjectType in state.eventFieldMappings) {
      state.eventFieldMappings[crmObjectType].mappings = [
        ...state.eventFieldMappings[crmObjectType].mappings,
        emptyFieldMapping
      ];
    }
  },
  removeEventFieldMappingItem(
    state: State,
    { crmObjectType, fieldMappingID }: { crmObjectType: string; fieldMappingID: string }
  ) {
    if (crmObjectType in state.eventFieldMappings) {
      state.eventFieldMappings[crmObjectType].mappings = state.eventFieldMappings[crmObjectType].mappings.filter(
        fieldMapping => fieldMapping.id !== fieldMappingID
      );
    }
  },
  updateEventFieldMappingItem(
    state: State,
    { crmObjectType, updatedFieldMapping }: { crmObjectType: string; updatedFieldMapping: FieldMapping }
  ) {
    if (crmObjectType in state.eventFieldMappings) {
      state.eventFieldMappings[crmObjectType].mappings = state.eventFieldMappings[crmObjectType].mappings.map(
        fieldMapping => {
          if (fieldMapping.id === updatedFieldMapping.id) {
            return {
              ...fieldMapping,
              ...updatedFieldMapping
            };
          }
          return fieldMapping;
        }
      );
    }
  },
  discardEventFieldMappingChanges(state: State, crmObjectType: string) {
    if (!state?.eventFieldMappings[crmObjectType]?.mappings || !state?.prevEventFieldMappings[crmObjectType]) {
      return;
    }
    state.eventFieldMappings[crmObjectType].mappings = state.prevEventFieldMappings[crmObjectType];
    const defaultErrorArray = new Array(state.eventFieldMappings[crmObjectType].mappings.length).fill(null);
    state.eventFieldMappingRowError[crmObjectType] = defaultErrorArray;
  },
  setSaveEventFieldMappingLoading(
    state: State,
    {
      crmObjectType,
      isSaveEventFieldMappingLoading
    }: { crmObjectType: string; isSaveEventFieldMappingLoading: boolean }
  ) {
    state.isSaveEventFieldMappingLoading[crmObjectType] = isSaveEventFieldMappingLoading;
  }
};
